import React, { useMemo, useState } from "react";
import { Search, ArrowLeft, ChevronRight, Filter, Inbox } from "lucide-react";
import { severityClasses, actionClasses, shortTime, DEMO_DISCLAIMER } from "../../demo/demoData";
import { useDemo } from "../../demo/DemoContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";

const sevTone = { Critical: "red", High: "amber", Medium: "cyan", Low: "green" };

function Stat({ label, value, tone = "text-foreground" }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <p className={`font-heading text-2xl font-bold ${tone}`}>{value}</p>
      <p className="text-[0.72rem] text-muted-foreground leading-tight mt-0.5">{label}</p>
    </div>
  );
}

function Bars({ data, colorFor }) {
  const entries = Object.entries(data);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="space-y-2.5">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-3">
          <span className="w-40 text-xs text-muted-foreground shrink-0 truncate">{k}</span>
          <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
            <div className={`h-full rounded-full ${colorFor(k)}`} style={{ width: `${(v / max) * 100}%` }} />
          </div>
          <span className="w-6 text-right text-xs text-foreground">{v}</span>
        </div>
      ))}
    </div>
  );
}

export default function HeimdallDashboard({ focusId, onGenerate }) {
  const { incidents, stats, reviewIncident, markResolved } = useDemo();
  const [openId, setOpenId] = useState(focusId || null);
  const [query, setQuery] = useState("");
  const [sev, setSev] = useState("all");
  const [status, setStatus] = useState("all");
  const [action, setAction] = useState("all");
  const [note, setNote] = useState("");

  React.useEffect(() => { if (focusId) setOpenId(focusId); }, [focusId]);

  const filtered = useMemo(() => {
    return incidents.filter((i) => {
      const q = query.trim().toLowerCase();
      const mq = !q || i.id.toLowerCase().includes(q) || i.risk.toLowerCase().includes(q);
      return mq && (sev === "all" || i.severity === sev) && (status === "all" || i.status === status) && (action === "all" || i.action === action);
    });
  }, [incidents, query, sev, status, action]);

  const open = openId ? incidents.find((i) => i.id === openId) : null;

  if (open) {
    return (
      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-5 sm:p-7">
        <button onClick={() => setOpenId(null)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to incidents
        </button>
        <div className="mt-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-heading text-xl font-bold text-foreground">{open.id}</p>
            <p className="text-sm text-muted-foreground">{open.interactionType} \u00b7 {open.channel}</p>
          </div>
          <div className="flex gap-2">
            <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${severityClasses[sevTone[open.severity]]}`}>{open.severity}</span>
            <span className={`rounded-md border px-2.5 py-1 text-xs font-semibold ${actionClasses[open.action]}`}>{open.action}</span>
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Original synthetic interaction</p>
          <p className="mt-2 text-sm text-foreground font-mono">\u201c{open.input}\u201d</p>
        </div>

        <dl className="mt-4 grid gap-4 sm:grid-cols-2">
          {[["Detected risk", open.risk], ["Policy applied", `${open.policy} (${open.policyVersion})`], ["System action", open.action], ["Escalation route", open.escalationRoute], ["Status", open.status], ["Confidence", `${Math.round((open.confidence || 0) * 100)}%`]].map(([k, v]) => (
            <div key={k}><dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt><dd className="mt-0.5 text-sm text-foreground">{v}</dd></div>
          ))}
        </dl>

        <div className="mt-5 rounded-xl border border-primary/25 bg-primary/[0.05] p-4">
          <p className="text-sm font-semibold text-foreground">Add a reviewer decision</p>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Reviewer note (optional)" className="mt-3 bg-navy-800 border-white/12 resize-none" />
          <div className="mt-3 flex flex-wrap gap-2">
            {["Confirm issue", "False positive", "Escalate", "Mark reviewed"].map((d) => (
              <Button key={d} size="sm" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground" onClick={() => { reviewIncident(open.id, d, note); toast.success(`Decision recorded: ${d}`); }}>{d}</Button>
            ))}
            <Button size="sm" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground" onClick={() => { markResolved(open.id); toast.success("Incident marked resolved"); }}>Mark resolved</Button>
          </div>
          {open.reviewerDecision && <p className="mt-3 text-sm text-muted-foreground border-t border-white/10 pt-3">Current decision: <span className="text-foreground">{open.reviewerDecision}</span>{open.reviewerNotes ? ` \u2014 \u201c${open.reviewerNotes}\u201d` : ""}</p>}
        </div>

        <div className="mt-5">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => onGenerate && onGenerate(open.id)}>Generate evidence record <ChevronRight className="ml-0.5 h-4 w-4" /></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Total incidents" value={stats.total} />
        <Stat label="Open" value={stats.open} tone="text-foreground" />
        <Stat label="Awaiting review" value={stats.awaiting} tone="text-signal-amber" />
        <Stat label="Blocked" value={stats.blocked} tone="text-signal-red" />
        <Stat label="Redacted" value={stats.redacted} tone="text-primary" />
        <Stat label="Resolved" value={stats.resolved} tone="text-signal-green" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-white/10 bg-navy-900/60 p-5">
          <p className="text-sm font-semibold text-foreground">Severity distribution</p>
          <div className="mt-4"><Bars data={stats.bySeverity} colorFor={(k) => ({ Critical: "bg-signal-red", High: "bg-signal-amber", Medium: "bg-primary", Low: "bg-signal-green" }[k] || "bg-primary")} /></div>
        </div>
        <div className="rounded-xl border border-white/10 bg-navy-900/60 p-5">
          <p className="text-sm font-semibold text-foreground">Risk categories</p>
          <div className="mt-4"><Bars data={stats.byCategory} colorFor={() => "bg-signal-blue"} /></div>
        </div>
        <div className="rounded-xl border border-white/10 bg-navy-900/60 p-5">
          <p className="text-sm font-semibold text-foreground">Activity by channel</p>
          <div className="mt-4"><Bars data={stats.byChannel} colorFor={() => "bg-primary"} /></div>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-navy-900/60 overflow-hidden">
        <div className="p-4 border-b border-white/10 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search incident reference\u2026" className="pl-9 bg-navy-800 border-white/12" />
          </div>
          {[["Severity", sev, setSev, ["Critical", "High", "Medium", "Low"]], ["Status", status, setStatus, ["Open", "Awaiting review", "Escalated", "Resolved"]], ["Action", action, setAction, ["Block", "Redact", "Escalate", "Warn", "Hold", "Allow"]]].map(([label, val, set, opts]) => (
            <Select key={label} value={val} onValueChange={set}>
              <SelectTrigger className="w-auto min-w-[130px] bg-navy-800 border-white/12"><Filter className="h-3.5 w-3.5 mr-1 text-muted-foreground" /><SelectValue placeholder={label} /></SelectTrigger>
              <SelectContent className="bg-navy-800 border-white/12">
                <SelectItem value="all">All {label.toLowerCase()}</SelectItem>
                {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ))}
        </div>

        <div className="overflow-x-auto">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Inbox className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">No incidents match your filters.</p>
            </div>
          ) : (
            <table className="w-full border-collapse min-w-[640px]">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Reference</th>
                  <th className="px-4 py-3 font-medium">Risk</th>
                  <th className="px-4 py-3 font-medium">Severity</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Time</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.id} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => setOpenId(i.id)}>
                    <td className="px-4 py-3 text-sm font-mono text-foreground">{i.id}</td>
                    <td className="px-4 py-3 text-sm text-foreground max-w-[220px] truncate">{i.risk}</td>
                    <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold ${severityClasses[sevTone[i.severity]]}`}>{i.severity}</span></td>
                    <td className="px-4 py-3"><span className={`rounded-md border px-2 py-0.5 text-[0.7rem] font-semibold ${actionClasses[i.action]}`}>{i.action}</span></td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{i.status}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{shortTime(i.createdAt)}</td>
                    <td className="px-4 py-3 text-right"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground/80">{DEMO_DISCLAIMER} All totals and charts are calculated from seeded demonstration incidents.</p>
    </div>
  );
}
